import React, { useCallback, useEffect, useState } from 'react';
import { AppRemixIcon, AppNoInfo } from '@/layout/AppComponents';
import styled from 'styled-components';
import { Scrollbars } from 'react-custom-scrollbars';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import colors from '../../../../../../constants/colors';
import minions from '../../../../../../minions/minions';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';
import GGCLEvidenceNewForm from './Evidence/GGCLEvidenceNewForm';
import GGCLEvidenceEditForm from './Evidence/GGCLEvidenceEditForm';
import NotificationAlert from '../../../../../components/helpers/notificationAlert';
import Translator from '../../../../../components/helpers/Translator';
import errorHandler from '@/layout/components/error/ErrorHandler';

export default function GGCLPlantSelectedEvidence(props) {
  const { obligationID, plantIndex, evidencesPermissions } = props;
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector((s) => s?.auth?.user?.id);
  const permissions = useSelector((s) => s.module[moduleBridgeHex].permissions);
  const userBridgeSettings = useSelector(
    (s) => s.module?.[moduleBridgeHex]?.userBridge?.permissions
  );
  const poLevel3 = useSelector(
    (s) => s.currentPage?.plantObligationLevels?.level3
  );
  const plantListTab = useSelector((s) => s.currentPage?.plantListTab);
  const plant = useSelector(
    (s) => s.currentPage?.plantListTab?.[obligationID][plantIndex]
  );
  const evidences = useSelector(
    (s) => s.currentPage?.evidences?.[plant?.plantObligationID]
  );
  const [showAddNewEvidence, setShowAddNewEvidence] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isEditingForbidden = () =>
    (userBridgeSettings?.level === 2 &&
      !userBridgeSettings?.evidences?.write) ||
    poLevel3?.[plant?.plantObligationID]?.alerts?.corporateObligation ||
    (userId !== selectedEvidence.userId &&
      evidencesPermissions?.editOnlyByUser &&
      evidencesPermissions?.editOnlyByUser !== false &&
      (!evidencesPermissions?.editOnlyByUser?.editOnlyByUserExceptions?.users ||
        !evidencesPermissions?.editOnlyByUser?.editOnlyByUserExceptions?.users.includes(
          userId
        )));

  // > LOAD EVIDENCES
  const loadEvidences = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_globalLegalCompliance_evidences_list"),
          {
            moduleBridgeHex,
            plantHex: plant.hex,
            plantObligationID: plant?.plantObligationID,
          }
        )
        .then((r) =>
          dispatch(
            currentPageAction.change("evidences", {
              [plant?.plantObligationID]: r.data,
            })
          )
        )
        .catch((e) => dispatch(errorHandler(e))),
    [plantListTab, plant, obligationID, moduleBridgeHex]
  );

  useEffect(() => {
    loadEvidences();
  }, []);
  // < LOAD EVIDENCES

  const handleDownloadFile = useCallback(
    async (url = null) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_globalLegalCompliance_evidences_download"
          ),
          {
            evidenceID: selectedEvidence.id,
            plantHex: plant.hex,
            url,
          }
        )
        .then((r) => window.open(r.data, "_blank"))
        .catch((e) => dispatch(errorHandler(e))),
    [selectedEvidence, plant]
  );

  // < ADD EVIDENCE
  const handleAddEvidence = useCallback(
    async (payload) => {
      if (isLoading) return;
      setIsLoading(true);
      axios
        .post(
          axiosPath("api_r_user_module_globalLegalCompliance_evidences_upload"),
          minions.forms.toFormData({
            ...payload,
            moduleBridgeHex,
            plantHex: plant.hex,
            plantObligationID: plant?.plantObligationID,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then((r) => {
          dispatch(
            currentPageAction.change("evidences", {
              [plant?.plantObligationID]: r.data.evidences,
            })
          );
          setShowAddNewEvidence(false);
          setIsLoading(false);
        })
        .catch((e) => {
          setIsLoading(false);
          dispatch(errorHandler(e));
        });
    },
    [isLoading, plant, moduleBridgeHex, permissions, poLevel3]
  );
  // < ADD EVIDENCE

  // < EDIT EVIDENCE
  const handleEditEvidence = useCallback(
    async (payload) => {
      if (isLoading) return;
      setIsLoading(true);
      axios
        .post(
          axiosPath("api_r_user_module_globalLegalCompliance_evidences_edit"),
          minions.forms.toFormData({
            ...payload,
            moduleBridgeHex,
            plantHex: plant.hex,
            plantObligationID: plant?.plantObligationID,
            evidenceID: selectedEvidence.id,
          }),
          { headers: { "Content-Type": "multipart/form-data" } }
        )
        .then((r) => {
          dispatch(
            currentPageAction.change("evidences", {
              [plant?.plantObligationID]: r.data.evidences,
            })
          );
          setSelectedEvidence(
            r.data?.evidences.find((e) => e.id === selectedEvidence.id)
          );
          setIsEditMode(false);
          setIsLoading(false);
        })
        .catch((e) => {
          setIsLoading(false);
          dispatch(errorHandler(e));
        });
    },
    [isLoading, plant, permissions, poLevel3, moduleBridgeHex, selectedEvidence]
  );
  // < EDIT EVIDENCE

  // > REMOVE EVIDENCE
  const handleRemove = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    await axios
      .post(
        axiosPath("api_r_user_module_globalLegalCompliance_evidences_remove"),
        {
          moduleBridgeHex,
          plantHex: plant.hex,
          plantObligationID: plant?.plantObligationID,
          evidenceID: selectedEvidence.id,
        }
      )
      .then((r) => {
        dispatch(
          currentPageAction.change("evidences", {
            [plant?.plantObligationID]: evidences.filter(
              (e) => e.id !== selectedEvidence.id
            ),
          })
        );
        setSelectedEvidence(false);
        setIsEditMode(false);
        setIsLoading(false);
      })
      .catch((e) => {
        setIsLoading(false);
        NotificationAlert({
          title: ReactDOMServer.renderToString(
            <Translator file="user" name="user.error" />
          ),
          message: ReactDOMServer.renderToString(
            <Translator
              file="module-legalCompliance"
              name="error.evidence.delete"
            />
          ),
          type: "danger",
          position: "top-center",
          duration: 0,
        });
        dispatch(errorHandler(e));
      });
  }, [
    isLoading,
    plant,
    permissions,
    poLevel3,
    selectedEvidence,
    moduleBridgeHex,
  ]);
  // < REMOVE EVIDENCE

  if (!evidences) return <AppNoInfo minHeight={300} />;

  return (
    <EvidenceTabContainer>
      <SidebarMenu>
        <>
          {(userBridgeSettings?.level !== 2 ||
            userBridgeSettings?.evidences?.write === true) &&
            !poLevel3?.[plant?.plantObligationID]?.alerts
              ?.corporateObligation && (
              <span
                className="add-button"
                onClick={() => {
                  setShowAddNewEvidence(true);
                  setSelectedEvidence(false);
                  setIsEditMode(false);
                }}
              >
                <Translator
                  file="module-legalCompliance"
                  name="module.lc.new-evidence"
                />
              </span>
            )}
          {evidences.length > 0 ? (
            <Scrollbars style={{ height: '365px' }}>
              <ul>
                {evidences.map((evidence) => (
                  <li
                    key={evidence.id}
                    className={
                      selectedEvidence && selectedEvidence.id === evidence.id
                        ? 'ev-selected'
                        : ''
                    }
                    onClick={() => {
                      setShowAddNewEvidence(false);
                      setSelectedEvidence(evidence);
                      setIsEditMode(false);
                    }}
                  >
                    <div className="first-row">
                      <span
                        className="name"
                        title={evidence.name || evidence.description}
                      >
                        {evidence.name || evidence.description}
                      </span>
                      <AppRemixIcon
                        className="mr-2"
                        icon="attachment-2"
                        noVariants
                        color={
                          evidence.url || evidence?.urls?.length > 0
                            ? colors.primary
                            : colors.grey400
                        }
                      />
                    </div>
                    <div className="second-row">
                      <span className="sr-date">{evidence.date}</span>
                      <span className="sr-user mr-2">{evidence.user}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </Scrollbars>
          ) : (
            <AppNoInfo
              text={
                <Translator file="module-legalCompliance" name="no.evidences" />
              }
              color={colors.grey400}
              icon={
                <AppRemixIcon
                  size={24}
                  icon="file-search"
                  color={colors.grey400}
                />
              }
            />
          )}
        </>
      </SidebarMenu>
      <Content>
        {showAddNewEvidence ? (
          <GGCLEvidenceNewForm
            handleAddEvidence={handleAddEvidence}
            isLoading={isLoading}
          />
        ) : (
          <>
            {selectedEvidence ? (
              <>
                {isEditMode ? (
                  <GGCLEvidenceEditForm
                    selectedEvidence={selectedEvidence}
                    handleEditEvidence={handleEditEvidence}
                    onCancel={() => {
                      setSelectedEvidence(null);
                      setIsEditMode(false);
                    }}
                    onRemove={handleRemove}
                    isLoading={isLoading}
                  />
                ) : (
                  <>
                    <h3>
                      <Translator
                        file="module-legalCompliance"
                        name="evidence.information.name"
                        variables={{ name: selectedEvidence.name }}
                      />
                      <span
                        className="float-right pointer mr-2"
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-legalCompliance"
                            name="close"
                          />
                        )}
                      >
                        <AppRemixIcon
                          icon="close"
                          size={16}
                          onClick={() => {
                            setSelectedEvidence(null);
                            setIsEditMode(false);
                          }}
                        />
                      </span>
                      {isEditingForbidden() ? (
                        ''
                      ) : (
                        <span
                          className="float-right pointer mr-2"
                          title={ReactDOMServer.renderToString(
                            <Translator file="general" name="general.edit" />
                          )}
                        >
                          <AppRemixIcon
                            icon="edit"
                            className="float-right pointer mr-2"
                            size={16}
                            onClick={() => setIsEditMode(true)}
                          />
                        </span>
                      )}
                    </h3>
                    <EvidenceInfo className="mt-4">
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="name"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.name}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="associated.files"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.urls &&
                          selectedEvidence.urls.length > 0 ? (
                            selectedEvidence.urls.map((url) => (
                              <FileDownload
                                key={url}
                                onClick={() => handleDownloadFile(url)}
                                title={ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-legalCompliance"
                                    name="download.file"
                                  />
                                )}
                              >
                                {minions.files.fileIcon(
                                  url,
                                  true,
                                  null,
                                  'mr-2'
                                )}
                                {url}
                              </FileDownload>
                            ))
                          ) : (
                            <span>
                              <Translator
                                file="module-legalCompliance"
                                name="no.associated.files"
                              />
                            </span>
                          )}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="user"
                            name="user.obligation.evidence.date"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.date ? (
                            selectedEvidence.date
                          ) : (
                            <span className="text-muted">
                              <Translator file="user" name="user.no-date" />
                            </span>
                          )}
                        </span>
                      </li>
                      <li>
                        <span className="eventinfo-title">
                          <Translator
                            file="module-legalCompliance"
                            name="module.lc.evidence.description"
                          />
                        </span>
                        <span className="eventinfo-content">
                          {selectedEvidence.description}
                        </span>
                      </li>
                    </EvidenceInfo>
                  </>
                )}
              </>
            ) : (
              <AppNoInfo
                text={
                  <>
                    <span>
                      <Translator
                        file="module-legalCompliance"
                        name="select.evidence.or.create.button"
                      />
                      <b>
                        {' '}
                        <Translator
                          file="module-legalCompliance"
                          name="module.lc.new-evidence"
                        />
                      </b>
                      .
                    </span>
                  </>
                }
                color={colors.grey400}
                icon={
                  <AppRemixIcon
                    size={40}
                    icon="file-search"
                    color={colors.grey400}
                  />
                }
              />
            )}
          </>
        )}
      </Content>
    </EvidenceTabContainer>
  );
}

const EvidenceTabContainer = styled.div`
  min-height: 325px;
  display: grid;
  grid-template-columns: 350px 1fr;
`;

const SidebarMenu = styled.div`
  overflow-y: auto;
  padding: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  .add-button {
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid ${colors.primary};
    color: ${colors.primary};
    margin: 0;
    height: 32px;
    cursor: pointer;
  }
  ul {
    padding: 4px;
    //height: 268px;
    overflow-y: auto;
    li {
      padding: 4px 2px;
      border-bottom: 1px solid ${colors.grey300};
      cursor: pointer;
      &.ev-selected {
        background: ${colors.grey300};
      }
      .first-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: ${colors.primary};
        .name {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
      .second-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 10px;
        .sr-date {
          letter-spacing: 1px;
        }
        .sr-user {
          color: ${colors.primary};
        }
      }
    }
  }
`;

const Content = styled.div`
  border-left: 1px solid ${colors.grey300};
  padding: 4px;
  .row-no-margin > .row {
    margin: 0;
  }
`;
const EvidenceFileList = styled.ul`
  list-style: none;
  margin: 0;
  padding-left: 6px;
  li {
    cursor: pointer;
    display: flex;
    align-items: center;
    border-top: 1px solid ${colors.grey300};
    padding: 8px 0;
    span {
      font-size: 12px;
      color: ${colors.primary};
    }
  }
  li:first-child {
    border-top: 0;
  }
`;

const NoFilesCard = styled.div`
  border: 1px solid ${colors.grey300};
`;

const EvidenceInfo = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  height: 350px;
  overflow: auto;
  li {
    padding: 8px 0;
    border-top: 1px solid ${colors.grey300};
    display: flex;
    align-items: baseline;
    .eventinfo-title {
      display: inline-block;
      width: 200px;
      text-transform: uppercase;
      font-weight: bold;
      color: ${colors.primary};
    }
  }
`;

const FileDownload = styled.div`
  &:hover {
    cursor: pointer;
  }
`;
